import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Globe2, Monitor, Smartphone } from 'lucide-react';
import GlassCard from '@/components/ui/GlassCard';
import { appMeta, subscriptionPlans } from '@/config/iphonefastflip';

const platforms = [
  {
    name: 'Przeglądarka',
    icon: Globe2,
    status: 'Gotowa do testów',
    description: 'Bez instalacji. Układ automatycznie dopasowuje się do telefonu, tabletu lub komputera.',
    action: true,
  },
  {
    name: 'Android',
    icon: Smartphone,
    status: 'Interfejs mobilny',
    description: 'Dolna nawigacja, dotykowe karty i widoki zoptymalizowane dla wąskich ekranów.',
    action: false,
  },
  {
    name: 'Windows',
    icon: Monitor,
    status: 'Interfejs desktopowy',
    description: 'Panel boczny, skróty klawiaturowe, gęstsza siatka oraz dzielony widok szczegółów.',
    action: false,
  },
] as const;

const DownloadSection: React.FC = () => (
  <>
    <section id="platformy" className="scroll-mt-24 px-4 py-20 sm:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto mb-12 max-w-3xl text-center">
          <p className="section-eyebrow">Jedna aplikacja, właściwy interfejs</p>
          <h2 className="section-title">Telefon dostaje układ Androida. Komputer — pulpit Windows.</h2>
          <p className="section-copy">
            Responsywność nie ogranicza się do zwężenia kart. Zmienia się nawigacja, gęstość informacji i sposób otwierania szczegółów ogłoszenia.
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-3">
          {platforms.map((platform, index) => {
            const Icon = platform.icon;
            return (
              <motion.div
                key={platform.name}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <GlassCard className={`h-full p-7 ${platform.action ? 'border-blue-400/25' : ''}`}>
                  <div className="mb-6 flex items-start justify-between gap-4">
                    <div className="grid h-14 w-14 place-items-center rounded-2xl bg-blue-500/10 text-blue-300">
                      <Icon className="h-7 w-7" />
                    </div>
                    <span className="rounded-full border border-white/10 bg-white/[0.035] px-3 py-1 text-xs font-semibold text-slate-400">
                      {platform.status}
                    </span>
                  </div>
                  <h3 className="text-2xl font-bold text-white">{platform.name}</h3>
                  <p className="mt-3 min-h-[72px] text-sm leading-6 text-slate-400">{platform.description}</p>
                  {platform.action ? (
                    <a href={appMeta.webUrl} className="button-primary mt-6 w-full">
                      Uruchom teraz <ArrowRight className="h-4 w-4" />
                    </a>
                  ) : (
                    <div className="mt-6 flex min-h-12 items-center gap-2 rounded-xl border border-white/[0.07] px-4 text-sm text-slate-500">
                      <Check className="h-4 w-4 text-emerald-400" /> Ten sam zestaw funkcji
                    </div>
                  )}
                </GlassCard>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    <section id="plany" className="scroll-mt-24 px-4 py-20 sm:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="mb-12 max-w-3xl">
          <p className="section-eyebrow">Plany w aplikacji</p>
          <h2 className="section-title">Zacznij bezpłatnie i zwiększaj limity wtedy, gdy ich potrzebujesz.</h2>
          <p className="section-copy">
            Poniższe ceny pochodzą z bieżącej konfiguracji backendu iPhoneFastFlip. Płatne plany są obsługiwane przez Stripe Checkout oraz portal rozliczeniowy.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {subscriptionPlans.map((plan, index) => (
            <div
              key={plan.name}
              className={`rounded-2xl border p-5 ${
                index === 2
                  ? 'border-blue-400/35 bg-blue-500/[0.08] shadow-[0_18px_60px_rgba(59,130,246,0.12)]'
                  : 'border-white/[0.08] bg-white/[0.025]'
              }`}
            >
              {index === 2 && <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.2em] text-blue-300">Popularny</p>}
              <h3 className="text-lg font-bold text-white">{plan.name}</h3>
              <p className="mt-2 text-xl font-black text-white">{plan.price}</p>
              <p className="mt-4 text-sm leading-6 text-slate-400">{plan.description}</p>
            </div>
          ))}
        </div>
        <p className="mt-5 text-xs leading-5 text-slate-500">
          Ostateczny zakres funkcji, limity i kwota są zawsze pokazane przed rozpoczęciem płatności w aplikacji.
        </p>
      </div>
    </section>
  </>
);

export default DownloadSection;
